import React, { CSSProperties, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EditorHotbarContext } from "./EditorHotbar";
import FogIcon from "../Icons/FogIcon";
import { GRID_SIZE } from "./Grid";
import { Rootstate } from "../../store";
import { setFogShape } from "../../reducers/map/mapReducerActions";
import Input from "../UI/Input";

const FogPopup = () => {
    const [shape, setShape] = useState<'sphere' | 'cube' | undefined>(undefined);
    const [size, setSize] = useState<number>(10);
    const [add, setAdd] = useState(false);
    const dispatch = useDispatch();

    const zoom = useSelector((state: Rootstate) => state.zoom.zoom);


    const [style, setStyle] = useState<CSSProperties>({
        transformOrigin: 'left top',
    });

    useEffect(() => {
        if(shape) {
            dispatch(setFogShape({
                type: shape,
                size,
                add,
            }));
        } else {
            dispatch(setFogShape(undefined));
        }
    }, [shape, size, add]);

    useEffect(() => {
        return () => {
            dispatch(setFogShape(undefined));
        };
    }, []);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            setStyle((oldStyle) => ({
                ...oldStyle,
                top: e.pageY,
                left: e.pageX,
            }));
        };

        window.addEventListener('mousemove', onMouseMove);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    const actualTransform =
    `scale(${zoom})`;

    return (
        <>
            {!Number.isNaN(size) && shape !== undefined && (
                <div
                    style={{
                        ...style,
                        transform: actualTransform,
                    }}
                    className='fixed pointer-events-none z-0'
                >
                    <svg
                        height={`${(GRID_SIZE * (size / 5))}`}
                        width={`${(GRID_SIZE * (size / 5))}`}
                        className='overflow-visible'
                    >
                        {shape === 'sphere'
                            ? (
                                <circle
                                    cx={0}
                                    cy={0}
                                    r={(size / 10) * GRID_SIZE}
                                    className={`${add ? 'fill-black' : 'fill-white'} opacity-50`}
                                />
                            ) : (
                                <polygon
                                    points={
                                        ` -${size / 5 * GRID_SIZE / 2}, -${size / 5 * GRID_SIZE / 2}`
                                        + ` ${size / 5 * GRID_SIZE / 2}, -${size / 5 * GRID_SIZE / 2}`
                                        + ` ${size / 5 * GRID_SIZE / 2}, ${size / 5 * GRID_SIZE / 2}`
                                        + ` -${size / 5 * GRID_SIZE / 2}, ${size / 5 * GRID_SIZE / 2}`
                                    }
                                    className={`${add ? 'fill-black' : 'fill-white'} opacity-50`}
                                />
                            )}
                    </svg>
                </div>
            )}
            <div className="rounded-bl-xl p-3 bg-mid-blue relative  z-1">
                <div className="rounded-xl p-3 bg-deep-blue flex flex-col gap-3">
                    <label>
                        Fog of war
                    </label>
                    <div>
                        <label htmlFor="size" className="block mt-1 mb-1 text-xs font-medium">
                            Size (feet)
                        </label>
                        <Input
                            type="number"
                            id="size"
                            value={size}
                            min={1}
                            max={100}
                            onChange={(e) => {
                                setSize(parseInt(e.target.value));
                            }}
                            className={"w-24 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg"
                                + " focus:ring-blue-500 focus:border-blue-500 block p-2.5 bg-gray-700"
                                + " border-gray-600 placeholder-gray-400 text-white "
                                + " focus:ring-blue-500 focus:border-blue-500"}
                            placeholder="0"
                            required
                        />
                    </div>
                    <div>
                        <label>
                            Brush
                        </label>
                        <div className="flex gap-2">
                            <button
                                className={'hover:bg-gray-700 hover:text-white w-12 h-12'
                                    + ' rounded-md px-2 py-1 text-sm font-medium'
                                    + ` ${shape === 'sphere' ? 'bg-gray-700' : ''}`}
                                onClick={() => setShape(shape !== 'sphere' ? 'sphere' : undefined)}
                            >
                                <div className="bg-white w-8 h-8 rounded-xl" />
                            </button>
                            <button
                                className={'hover:bg-gray-700 hover:text-white w-12 h-12'
                                    + ' rounded-md px-2 py-1 text-sm font-medium'
                                    + ` ${shape === 'cube' ? 'bg-gray-700' : ''}`}
                                onClick={() => setShape(shape !== 'cube' ? 'cube' : undefined)}
                            >
                                <div className="bg-white w-8 h-8 rounded-xs" />
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col items-start gap-y-0 mt-3">
                        <span>
                            Add fog
                        </span>
                        <label className="relative inline-flex items-center justify-center cursor-pointer">
                            <Input
                                type="checkbox"
                                onChange={() => setAdd(!add)}
                                checked={add}
                                value=""
                                className="sr-only peer"
                            />
                            <div
                                className={"w-11 h-6 bg-gray-200 "
                                    + " rounded-xl"
                                    + " bg-gray-700 peer-checked:after:translate-x-full"
                                    + " peer-checked:after:border-white after:content-[''] after:absolute"
                                    + " after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300"
                                    + " after:border after:rounded-xl after:h-5 after:w-5 after:transition-all"
                                    + " border-gray-600 peer-checked:bg-blue-600"}
                            />
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
};

const FogEditor = () => {
    const { setActiveElement, id } = useContext(EditorHotbarContext);

    const handleClick = () => {
        const popup = (
            <FogPopup />
        );
        setActiveElement('fog-editor', (
            <>
                {popup}
            </>
        ));
    };

    return (
        <>
            <div
                className={
                    'flex items-center cursor-pointer justify-center h-16 min-w-18'
                    + ` ${id === 'fog-editor' ? 'bg-mid-blue' : ''}`}
                onClick={handleClick}
            >
                <div className="">
                    <FogIcon className="h-9" />
                </div>
            </div>
        </>
    );
};

export default FogEditor;
